import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Clock, BookOpen, Users, Target, 
  MessageCircle, ChevronDown, ChevronUp, Printer, Download, Loader2,
  CheckCircle, Lightbulb, Package, Presentation, Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import AppShell from '@/components/AppShell';
import PrintHeader from '@/components/PrintHeader';
import PrintReadyMarker from '@/components/PrintReadyMarker';
import PdfCoverDialog from '@/components/PdfCoverDialog';
import { useDownloadPdf } from '@/hooks/useDownloadPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LectureNotes() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLecture, setExpandedLecture] = useState(1);

  useEffect(() => {
    fetchUnit();
  }, [unitId]);

  const fetchUnit = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/units/${unitId}`);
      setUnit(response.data);
    } catch (error) {
      toast.error('Failed to load lecture notes');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const { downloading: downloadingPdf, downloadPdf } = useDownloadPdf();
  const [coverOpen, setCoverOpen] = useState(false);
  const handleDownloadPdf = () => {
    if (!unitId) return;
    setCoverOpen(true);
  };
  const handleCoverConfirm = async (cover) => {
    const filename = `lecture-notes-${(unit?.title || 'unit').replace(/[^a-z0-9-]+/gi, '-').toLowerCase().slice(0, 40)}.pdf`;
    await downloadPdf({
      resource: 'lecture-notes',
      resourceId: unitId,
      filename,
      cover: {
        ...cover,
        title: unit?.title,
        grade: unit?.grade_level ? String(unit.grade_level) : '',
        unit_number: unit?.unit_number != null ? String(unit.unit_number) : '',
        resource_label: 'Lecture Notes',
      },
    });
    setCoverOpen(false);
  };

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#14213D] border-t-transparent"></div>
            <p className="mt-4 text-sm text-[#5A6577]">Loading lecture notes...</p>
          </div>
        </div>
      </AppShell>
    );
  }

  if (!unit || !unit.lecture_notes) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Card className="p-8 text-center max-w-md">
            <BookOpen className="h-16 w-16 text-[#CBD5E0] mx-auto mb-4" />
            <h2 className="font-serif-display text-xl text-[#14213D] mb-2">No Lecture Notes Available</h2>
            <p className="text-sm text-[#5A6577] mb-4">Lecture notes have not been generated for this unit yet.</p>
            <Button onClick={() => navigate(`/unit/${unitId}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Unit
            </Button>
          </Card>
        </div>
      </AppShell>
    );
  }

  const { lecture_notes } = unit;

  return (
    <AppShell>
      <div className="print:bg-white">
        <PrintReadyMarker ready={!loading && !!unit && !!unit.lecture_notes} />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 print:pt-0">
          <PrintHeader
            resourceType="Lecture Notes"
            resourceTitle={unit.title}
            grade={unit.grade_level}
            unitNumber={unit.unit_number}
          />
        </div>
        {/* Inline action bar */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between flex-wrap gap-3 print:hidden">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/unit/${unitId}`)}
              className="text-[#5A6577] hover:text-[#14213D]"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Unit
            </Button>
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] font-semibold">Lecture Notes</p>
              <p className="text-xs text-[#5A6577]">Grade {unit.grade_level} &middot; Unit {unit.unit_number}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/lecture-presentation/${unitId}`)} data-testid="present-btn">
              <Presentation className="h-4 w-4 mr-2" />
              Present
            </Button>
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-btn">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              data-testid="download-pdf-btn"
              title="Download these lecture notes as a PDF"
            >
              {downloadingPdf ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />PDF&hellip;</>
              ) : (
                <><Download className="h-4 w-4 mr-2" />PDF</>
              )}
            </Button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 print:py-4">
          {/* Header */}
          <div className="mb-8 print:mb-4">
            <h2 className="font-serif-display text-3xl text-[#14213D] tracking-tight mb-2 print:text-2xl">
              {unit.title}
            </h2>
            <div className="flex items-center gap-4 text-sm text-[#5A6577]">
              <Badge variant="outline" className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {lecture_notes.total_duration}
              </Badge>
              <span>4 Mini Lectures &bull; 8 Discussion Questions</span>
            </div>
          </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 print:grid-cols-2 print:gap-2 print:mb-4">
          {/* Learning Objectives */}
          <Card className="p-4 print:p-2">
            <h3 className="text-sm font-semibold text-[#14213D] mb-3 flex items-center gap-2">
              <Target className="h-4 w-4 text-green-600" />
              Learning Objectives
            </h3>
            <ul className="space-y-2 text-sm text-[#2D3748]">
              {lecture_notes.learning_objectives?.map((obj) => (
                <li key={obj.slice(0, 40)} className="flex gap-2">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{obj}</span>
                </li>
              ))}
            </ul>
          </Card>

          {/* Materials Needed */}
          <Card className="p-4 print:p-2">
            <h3 className="text-sm font-semibold text-[#14213D] mb-3 flex items-center gap-2">
              <Package className="h-4 w-4 text-blue-600" />
              Materials Needed
            </h3>
            <ul className="space-y-1 text-sm text-[#2D3748]">
              {lecture_notes.materials_needed?.map((mat) => (
                <li key={mat.slice(0, 40)} className="flex gap-2">
                  <span className="text-[#A0AEC0]">•</span>
                  <span>{mat}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        {/* Mini Lectures */}
        <div className="space-y-4 print:space-y-2">
          <h3 className="text-lg font-serif font-normal text-[#14213D] flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Mini Lectures
          </h3>

          {lecture_notes.mini_lectures?.map((lecture) => (
            <Card 
              key={lecture.lecture_number} 
              className="overflow-hidden print:break-inside-avoid"
              data-testid={`mini-lecture-${lecture.lecture_number}`}
            >
              {/* Lecture Header */}
              <div 
                className="p-4 bg-[#F9F8F4] cursor-pointer hover:bg-[#F9F8F4] transition-colors print:cursor-default print:bg-white print:border-b"
                onClick={() => setExpandedLecture(
                  expandedLecture === lecture.lecture_number ? null : lecture.lecture_number
                )}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#14213D] text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {lecture.lecture_number}
                    </div>
                    <div>
                      <h4 className="font-medium text-[#14213D]">{lecture.title}</h4>
                      <div className="flex items-center gap-2 text-sm text-[#5A6577] mt-1">
                        <Clock className="h-3 w-3" />
                        {lecture.duration}
                        <span className="mx-1">•</span>
                        <MessageCircle className="h-3 w-3" />
                        {lecture.discussion_questions?.length || 0} questions
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="print:hidden">
                    {expandedLecture === lecture.lecture_number ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-sm text-[#5A6577] mt-2 ml-11">{lecture.overview}</p>
              </div>

              {/* Expanded Content */}
              {(expandedLecture === lecture.lecture_number || true) && (
                <div className={`p-4 border-t border-[#EBE7E0] ${expandedLecture !== lecture.lecture_number ? 'hidden print:block' : ''}`}>
                  {/* Key Points */}
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-[#14213D] mb-2">Key Points</h5>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-[#2D3748]">
                      {lecture.key_points?.map((point) => (
                        <li key={point.slice(0, 40)} className="flex gap-2">
                          <span className="text-slate-500">→</span>
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Lecture Content */}
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-[#14213D] mb-2">Lecture Content</h5>
                    <div className="bg-white border border-[#EBE7E0] rounded-lg p-4 text-sm text-[#2D3748] leading-relaxed">
                      {lecture.content?.split('\n').map((line, idx) => {
                        // Parse markdown-style formatting
                        const trimmedLine = line.trim();
                        
                        // Section headers (** text **)
                        if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
                          return (
                            <h6 key={idx} className="font-semibold text-[#14213D] mt-3 mb-1 first:mt-0">
                              {trimmedLine.replace(/\*\*/g, '')}
                            </h6>
                          );
                        }
                        
                        // Bullet points
                        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
                          const bulletContent = trimmedLine.replace(/^[•\-\*]\s*/, '');
                          // Handle inline bold
                          const parts = bulletContent.split(/(\*\*[^*]+\*\*)/g);
                          return (
                            <div key={idx} className="flex gap-2 ml-2 my-0.5">
                              <span className="text-slate-500">•</span>
                              <span>
                                {parts.map((part, pIdx) => {
                                  // Stable key: index + content prefix so re-renders match.
                                  const key = `${pIdx}-${part.slice(0, 16)}`;
                                  if (part.startsWith('**') && part.endsWith('**')) {
                                    return <strong key={key}>{part.replace(/\*\*/g, '')}</strong>;
                                  }
                                  return <React.Fragment key={key}>{part}</React.Fragment>;
                                })}
                              </span>
                            </div>
                          );
                        }
                        
                        // Empty lines
                        if (!trimmedLine) {
                          return <div key={idx} className="h-2" />;
                        }
                        
                        // Regular text with possible inline bold
                        const parts = trimmedLine.split(/(\*\*[^*]+\*\*)/g);
                        return (
                          <p key={idx} className="my-1">
                            {parts.map((part, pIdx) => {
                              if (part.startsWith('**') && part.endsWith('**')) {
                                return <strong key={`${idx}-pb-${pIdx}`}>{part.replace(/\*\*/g, '')}</strong>;
                              }
                              return part;
                            })}
                          </p>
                        );
                      })}
                    </div>
                  </div>

                  {/* Discussion Questions */}
                  <div className="mb-4">
                    <h5 className="text-sm font-semibold text-[#14213D] mb-2 flex items-center gap-2">
                      <MessageCircle className="h-4 w-4 text-yellow-600" />
                      Discussion Questions
                    </h5>
                    <div className="space-y-3">
                      {lecture.discussion_questions?.map((q) => (
                        <div key={q.question.slice(0, 40)} className="bg-[#FBF6E8] border border-[#D4AF37]/40 rounded-lg p-3">
                          <p className="font-medium text-[#14213D] text-sm">{q.question}</p>
                          <p className="text-xs text-[#5A6577] mt-1">
                            <span className="font-medium">Purpose:</span> {q.purpose}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Transition Note */}
                  {lecture.transition_note && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        <span className="font-semibold">Transition:</span> {lecture.transition_note}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>

        {/* Assessment Suggestions & Differentiation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 print:mt-4 print:grid-cols-2 print:gap-2">
          {lecture_notes.assessment_suggestions?.length > 0 && (
            <Card className="p-4 print:p-2">
              <h3 className="text-sm font-semibold text-[#14213D] mb-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-purple-600" />
                Assessment Suggestions
              </h3>
              <ul className="space-y-2 text-sm text-[#2D3748]">
                {lecture_notes.assessment_suggestions.map((suggestion) => (
                  <li key={suggestion.slice(0, 40)} className="flex gap-2">
                    <span className="text-purple-500">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {lecture_notes.differentiation_tips && (
            <Card className="p-4 print:p-2">
              <h3 className="text-sm font-semibold text-[#14213D] mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-orange-600" />
                Differentiation Tips
              </h3>
              <p className="text-sm text-[#2D3748]">{lecture_notes.differentiation_tips}</p>
            </Card>
          )}
        </div>
      </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            .print\\:hidden { display: none !important; }
            .print\\:block { display: block !important; }
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          }
        `}</style>
      </div>
      <PdfCoverDialog
        open={coverOpen}
        onOpenChange={setCoverOpen}
        onConfirm={handleCoverConfirm}
        downloading={downloadingPdf}
        context={{
          title: unit?.title,
          resource_label: 'Lecture Notes',
          grade: unit?.grade_level,
          unit_number: unit?.unit_number,
        }}
      />
    </AppShell>
  );
}
